import {
  Box,
  Stack,
  styled,
  Theme,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { FC, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { HBButton } from '../../HBButton/HBButton';

import { HBIcon } from '../../HBIcon/HBIcon';
import { HBIconButton } from '../../HBIconButton/HBIconButton';
import { HBLink } from '../../HBLink/HBLink';
import { HBNextImage } from '../../HBNextImage/HBNextImage';
import { HBCardWidgetProps } from './HBCardWidgetType';
import { fontWeights } from '@/core/theme';
import { dataLayer } from '@/core/utils/data-layer';
import { HBCountDownTimer } from '../../HBCountDownTimer/HBCountDownTimer';
import { phrasesMessages } from '@/core/translations/phrases.messages';
import { glyphy } from '@/core/utils/glyphy';
import productPathGenerator from '@/utils/productPathGenerator';
import vendorPathGenerator from '@/utils/vendorPathGenerator';

const TooltipStyle = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.primary.main,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.primary.main,
    marginBottom: `${theme.spacing(2)} !important`,
    padding: theme.spacing(3, 4),
    borderRadius: theme.spacing(2),
  },
}));

const countDownTimerStyle = {
  '& > span': (theme: Theme) => ({
    padding: theme.spacing(0),
    color: theme.palette.error.main,
    margin: 0,
    ...theme.typography.caption,
    fontWeight: fontWeights.fontWeightBold,
  }),
  '& > p': (theme: Theme) => ({
    color: theme.palette.error.main,
    margin: theme.spacing(0, 0.5),
    ...theme.typography.caption,
    fontSize: 18,
    fontWeight: fontWeights.fontWeightBold,
  }),
};

const PriceBoxStyle = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  height: 26,
}));

export enum PriceCalculationEnum {
  // [Display(Name = "قیمت محصول")]
  ProductPricing = 1126001,

  // [Display(Name = "کمپین")]
  Campaign = 1126002,

  // [Display(Name = "قیمت گذاری پلکانی ")]
  PriceTiering = 1126003,
}

export const HBCardWidget: FC<HBCardWidgetProps> = (props) => {
  const {
    data: {
      price,
      imageUrl,
      currencyName,
      discountPercentage,
      oldPrice,
      storeName,
      title,
      rate,
      vendorId,
      quantityHTML,
      hasPromotion,
      maximalPerOrder,
      hsin,
      slug,
      availability,
      latinName,
      minimalPerOrder,
      level,
    },
    type,
    noDivider,
    addToBasketTitle,
    specialOfferTitle,
    addToBasket,
    addLoading,
    count,
    removeLoading,
    removeFromBasket,
    noAction,
    unAvailableProductId,
    unavailableOnAddToBasket,
  } = props;

  const [showCampaign, setShowCampaign] = useState(hasPromotion);
  const { formatMessage } = useIntl();
  const { palette, breakpoints, spacing } = useTheme();
  const breakpointDownMd = useMediaQuery(breakpoints.down('md'));

  let minimal = minimalPerOrder;
  let maximal = maximalPerOrder;
  if (showCampaign && level?.calculationType === PriceCalculationEnum.Campaign) {
    minimal = level.minimalPerOrder;
    maximal = level.maximalPerOrder;
  }

  const hasDiscount =
    (!showCampaign && !!discountPercentage) || (showCampaign && !!level?.discountPercent);

  const isSearchResponsive = useMemo(
    () => type === 'SEARCH_AND_COLLECTION_RESULT' && breakpointDownMd,
    [type, breakpointDownMd],
  );

  const calcSizeWrapper = () => {
    switch (type) {
      case 'SEARCH_AND_COLLECTION_RESULT':
        return { width: breakpointDownMd ? 65 : 188, height: breakpointDownMd ? 86 : 188 };
      default:
        return { width: 188, height: 188 };
    }
  };

  const routing = useMemo(() => {
    return productPathGenerator({ hsin, slug });
  }, []);

  const calcWidth = () => {
    if (type === 'SEARCH_AND_COLLECTION_RESULT') return '100%';
    else return 240;
  };

  const handleDataLayer = (eventName: string) => {
    const quantity_stuck = eventName.includes('add')
      ? (count || 0) + (minimal || 1)
      : (count || 0) - (minimal || 1);
    dataLayer({
      eventName,
      currency: currencyName || '',
      items: [
        {
          item_name: title,
          item_id: hsin,
          price_original: oldPrice,
          price_by_discount: price,
          item_seller: storeName,
          quantity_stuck,
        },
      ],
    });
  };

  return (
    <HBLink href={noAction ? undefined : routing} target="_blank">
      <Stack
        spacing={2}
        width={calcWidth()}
        borderRight={(theme) =>
          !noDivider
            ? isSearchResponsive
              ? 'unset'
              : `1px solid ${theme.palette.grey[200]}`
            : 'unset'
        }
        pr={type === 'SEARCH_AND_COLLECTION_RESULT' ? 'unset' : 6}
        sx={{ userSelect: 'none' }}
      >
        <Box
          sx={{ cursor: 'pointer' }}
          display="flex"
          width="100%"
          flexDirection={isSearchResponsive ? 'row' : 'column'}
          alignItems={'flex-start'}
        >
          {!isSearchResponsive && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              zIndex={1}
              mb={1}
              height={24}
            >
              {/* campaign */}
              <Box>
                {showCampaign && hasPromotion && (
                  <Box display="flex" justifyContent="space-between">
                    <Stack spacing={1} direction="row" alignItems={'center'}>
                      <Typography
                        sx={{ fontWeight: fontWeights.fontWeightBold }}
                        variant="subtitle2"
                        color="error.main"
                      >
                        {specialOfferTitle}
                      </Typography>
                      <HBCountDownTimer
                        targetDate={new Date(level?.toDate || '0').getTime()}
                        linkText=""
                        maximumShowed="hours"
                        onFinished={() => setShowCampaign(false)}
                        sx={countDownTimerStyle}
                        textVariant="caption"
                      />
                    </Stack>
                  </Box>
                )}
              </Box>
              {/* add to basket */}
              <Box>
                {!unavailableOnAddToBasket &&
                  availability &&
                  !noAction &&
                  (type === 'BUSINESS_SLIDER_DEFAULT' ||
                    type === 'BUSINESS_SLIDER_WITH_SIDEBAR' ||
                    type === 'SEARCH_AND_COLLECTION_RESULT') &&
                  (!count ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                    >
                      <TooltipStyle
                        title={
                          <Typography variant="caption" color="common.white">
                            {addToBasketTitle}
                          </Typography>
                        }
                        placement="top"
                      >
                        <HBIconButton
                          data-test-id="add-to-cart"
                          sx={{
                            borderColor: 'primary.main',
                            borderWidth: 2,
                            zIndex: 8,
                            minWidth: 24,
                            width: 24,
                            height: 24,
                            '&:hover i': {
                              color: (theme) => `${theme.palette.common.white} !important`,
                            },
                          }}
                          variant="outlined"
                          icon="plus"
                          aria-label="add"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handleDataLayer('add_to_cart');
                            props?.data?.id &&
                              addToBasket?.({ productId: props.data.id, unAvailableProductId });
                          }}
                          iconStyle={{ color: palette.primary.main }}
                          iconSize={'small'}
                        />
                      </TooltipStyle>
                    </Box>
                  ) : (
                    <Stack
                      direction="row"
                      spacing={1}
                      bgcolor="common.white"
                      alignItems="center"
                      sx={{
                        borderRadius: 2,
                        p: 0,
                        bgcolor: 'primary.main',
                        width: 76,
                        justifyContent: 'space-between',
                      }}
                      onClick={(e) => e.preventDefault()}
                    >
                      <HBButton
                        variant="text"
                        size="small"
                        disabled={addLoading || maximal === count}
                        sx={{
                          minWidth: 30,
                          background: 'unset !important',
                          p: 0,
                          height: '24px !important',
                        }}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          handleDataLayer('add_to_cart');
                          props?.data?.id &&
                            addToBasket?.({ productId: props.data.id, unAvailableProductId });
                        }}
                      >
                        <HBIcon
                          type="plus"
                          style={{
                            color: maximal === count ? palette.text.disabled : palette.common.white,
                            marginTop: spacing(1),
                            fontSize: 14,
                          }}
                        />
                      </HBButton>
                      <Typography variant="caption" color="common.white">
                        {count}
                      </Typography>
                      <HBButton
                        disabled={removeLoading}
                        variant="text"
                        sx={{
                          minWidth: 30,
                          background: 'unset !important',
                          p: 0,
                          height: '24px !important',
                        }}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          handleDataLayer('remove_from_cart');
                          props?.data?.id && removeFromBasket?.(props.data.id);
                        }}
                      >
                        <HBIcon
                          type={'minus'}
                          style={{
                            color: palette.common.white,
                            marginTop: spacing(1),
                            fontSize: 14,
                          }}
                        />
                      </HBButton>
                    </Stack>
                  ))}
              </Box>
            </Box>
          )}
          <Box
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              position: 'relative',
              ...calcSizeWrapper(),
              my: type === 'SEARCH_AND_COLLECTION_RESULT' ? { xs: 4.5, md: 0 } : 0,
              mr: type === 'SEARCH_AND_COLLECTION_RESULT' ? { xs: 5.5, md: 'auto' } : 'auto',
              ml: type === 'SEARCH_AND_COLLECTION_RESULT' ? { xs: 'unset', md: 'auto' } : 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            borderRadius={4}
          >
            <HBNextImage
              width={imageUrl?.includes('.webp') ? 400 : 300}
              height={imageUrl?.includes('.webp') ? 400 : 300}
              quality={80}
              src={imageUrl || ''}
              alt={title || ''}
              data-test-id="product-card"
            />
          </Box>
          <Stack
            width={{
              xs: type === 'SEARCH_AND_COLLECTION_RESULT' ? 'calc(100% - 104px)' : '100%',
              sm: '100%',
            }}
            mt={isSearchResponsive ? 0 : 1}
            ml={isSearchResponsive ? 4 : 0}
          >
            {/* campaign in search result */}
            {showCampaign && hasPromotion && isSearchResponsive && (
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Stack spacing={1} direction="row" alignItems={'center'}>
                  <Typography sx={{ fontWeight: '700' }} variant="subtitle2" color="error.main">
                    {specialOfferTitle}
                  </Typography>
                  <HBCountDownTimer
                    targetDate={new Date(level?.toDate || '0').getTime()}
                    linkText=""
                    maximumShowed="hours"
                    onFinished={() => setShowCampaign(false)}
                    sx={countDownTimerStyle}
                  />
                </Stack>
              </Box>
            )}
            {/* start --- title */}
            {title && (
              <Typography
                component="h3"
                variant={'subtitle2'}
                color="text.primary"
                mb={type === 'SEARCH_AND_COLLECTION_RESULT' ? { xs: 4, md: 1 } : 1}
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  width: '100%',
                  textAlign: 'left',
                  whiteSpace: 'pre-wrap',
                  maxHeight: 44,
                  height: 44,
                  WebkitLineClamp: '2',
                  WebkitBoxOrient: 'vertical',
                  display: '-webkit-box',
                }}
              >
                {title}
              </Typography>
            )}

            <Box display="flex" justifyContent="space-between" alignItems="center">
              {/* rate */}
              <Box height={26}>
                {!!rate && (
                  <Box
                    display="flex"
                    alignItems="center"
                    mb={type === 'SEARCH_AND_COLLECTION_RESULT' ? { xs: 4, md: 1 } : 1}
                  >
                    <HBNextImage
                      alt="favorite"
                      src="/assets/svg/favorite.svg"
                      placeholder="empty"
                      width={15}
                      height={15}
                    />
                    <Typography
                      variant="overline"
                      sx={{ color: 'secondary.main' }}
                      ml={1}
                      mt={0.75}
                    >
                      {rate?.toString()}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/*  quantity */}
              <Box>
                {availability && (
                  <Typography
                    variant="caption"
                    color="error.main"
                    display="flex"
                    alignItems={'center'}
                    mb={2}
                    component="div"
                    sx={{
                      '& .product-quantity': {
                        mx: 0.5,
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: quantityHTML! }}
                  />
                )}
              </Box>
            </Box>
            <Box height={20} mb={2}>
              {!!storeName && (
                <HBLink href={vendorPathGenerator({ vendorId, plaque: latinName })} passHref>
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    mb={2}
                    sx={{
                      '&:hover div, &:hover i': {
                        color: 'info.main',
                      },
                    }}
                  >
                    <HBIcon type="shop" sx={{ color: 'grey.500', fontSize: 14 }} size="small" />
                    <Typography
                      component="div"
                      variant="subtitle2"
                      color="grey.500"
                      ml={1}
                      sx={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        width: '100%',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {storeName}
                    </Typography>
                  </Box>
                </HBLink>
              )}
            </Box>
            {!unavailableOnAddToBasket && availability && (oldPrice || price) && (
              <PriceBoxStyle
                width={'100%'}
                justifyContent={hasDiscount ? 'space-between' : 'flex-end'}
              >
                {hasDiscount && (
                  <Typography
                    px={1}
                    py={0.5}
                    bgcolor={'error.main'}
                    color="common.white"
                    borderRadius={1.5}
                    component="div"
                    variant={isSearchResponsive ? 'overline' : 'subtitle2'}
                  >
                    {showCampaign && level
                      ? (Math.round((level.discountPercent || 0) * 10) / 10).toLocaleString()
                      : discountPercentage?.toLocaleString()}
                    %-
                  </Typography>
                )}
                <Stack alignItems={'center'} direction={'row'} spacing={1}>
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    width={
                      type === 'SEARCH_AND_COLLECTION_RESULT' ? { xs: '100%', sm: 'unset' } : '100%'
                    }
                    height={18}
                    mr={isSearchResponsive ? 2 : 'unset'}
                  >
                    {availability && ((!!discountPercentage && oldPrice) || showCampaign) && (
                      <Typography
                        component="div"
                        variant={isSearchResponsive ? 'caption' : 'subtitle2'}
                        color="text.disabled"
                        sx={{ textDecoration: 'line-through' }}
                      >
                        {Number(showCampaign ? level?.displayPrice : oldPrice).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant={isSearchResponsive ? 'subtitle2' : 'subtitle1'}
                    color="text.primary"
                    height={isSearchResponsive ? 20 : 26}
                    display="flex"
                    alignItems="center"
                    component="div"
                  >
                    {Number(showCampaign ? level?.finalPrice : price)?.toLocaleString()}
                    <Typography
                      component="span"
                      variant={isSearchResponsive ? 'caption' : 'subtitle2'}
                      color="text.primary"
                      ml={1}
                    >
                      {glyphy(currencyName)}
                    </Typography>
                  </Typography>
                </Stack>
              </PriceBoxStyle>
            )}

            {(!availability || unavailableOnAddToBasket) && (
              <Box display="flex" justifyContent={'flex-end'} width={'100%'} height={26}>
                <Typography color="text.secondary" variant="subtitle1" fontWeight="700">
                  {availability || unavailableOnAddToBasket
                    ? formatMessage(phrasesMessages.unavailable)
                    : quantityHTML}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </Stack>
    </HBLink>
  );
};
